import { createContext, createElement, useCallback, useContext, useEffect, useLayoutEffect, useState } from "react";
import type { ReactNode } from "react";

type Theme = "light" | "dark";

const STORAGE_KEY = "rtfm:theme";

const getPreferred = (): Theme =>
  window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
  set: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as Theme | null;
      return saved ?? "light";
    } catch {
      return "light";
    }
  });

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
  }, [theme]);

  useEffect(() => {
    const saved = ((): Theme | null => {
      try {
        return (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? null;
      } catch {
        return null;
      }
    })();
    if (!saved) {
      setTheme(getPreferred());
    }
  }, []);

  useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      const hasExplicit = (() => {
        try { return localStorage.getItem(STORAGE_KEY) != null; } catch { return false; }
      })();
      if (!hasExplicit) setTheme(e.matches ? "dark" : "light");
    };
    try {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    } catch {
      mql.addListener(handler);
      return () => mql.removeListener(handler);
    }
  }, []);

  const toggle = useCallback(() => {
    setTheme((t) => {
      const next: Theme = t === "dark" ? "light" : "dark";
      try { localStorage.setItem(STORAGE_KEY, next); } catch {}
      return next;
    });
  }, []);

  const set = useCallback((t: Theme) => {
    try { localStorage.setItem(STORAGE_KEY, t); } catch {}
    setTheme(t);
  }, []);

  return createElement(ThemeContext.Provider, { value: { theme, toggle, set } }, children);
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as Theme | null;
      return saved ?? "light";
    } catch {
      return "light";
    }
  });

  // Apply theme class to <html> pre-paint for instant variant updates
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
  }, [theme]);

  // Initialize to saved or system preference on first mount
  useEffect(() => {
    const saved = ((): Theme | null => {
      try {
        return (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? null;
      } catch {
        return null;
      }
    })();
    if (!saved) {
      setTheme(getPreferred());
    }
  }, []);

  // Respond to OS preference changes only if user hasn't explicitly chosen
  useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      const hasExplicit = (() => {
        try { return localStorage.getItem(STORAGE_KEY) != null; } catch { return false; }
      })();
      if (!hasExplicit) setTheme(e.matches ? "dark" : "light");
    };
    try {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    } catch {
      // Safari fallback
      mql.addListener(handler);
      return () => mql.removeListener(handler);
    }
  }, []);

  const toggle = useCallback(() => {
    setTheme((t) => {
      const next: Theme = t === "dark" ? "light" : "dark";
      try { localStorage.setItem(STORAGE_KEY, next); } catch {}
      return next;
    });
  }, []);

  const set = useCallback((t: Theme) => {
    try { localStorage.setItem(STORAGE_KEY, t); } catch {}
    setTheme(t);
  }, []);

  return { theme, toggle, set } as const;
};

